import { useQuery } from "@apollo/client";
import { Pagination, ToastContainer } from "pepsico-ds";
import { useContext, useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";
import {
  ListingComponentContext,
  ListingComponentProvider,
} from "../../components/common/listingComponents/ListingComponentContext";
import ListingComponentWrapper from "../../components/common/listingComponents/ListingComponentWrapper";
import { FileHistoryButton } from "../../components/fileManager";
import ConfirmationModal from "../../components/performances/ConfirmationModal";
import PerformanceDetailsDrawer from "../../components/performances/PerformanceDetailsDrawer";
import PerformancesTable from "../../components/performances/PerformancesTable";
import { getActivityTypeDisplayNames } from "../../components/performances/utils";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import LoaderContext from "../../context/LoaderContext";
import { listEngagements } from "../../graphql/queries/listEngagements";
import { loadUiComponent } from "../../graphql/queries/loadUiComponent";
import { useEngagementActions } from "../../hooks/useEngagementActions";
import { paths } from "../../routes/paths";
import { createPerformanceFilterTransform } from "./performanceFilterTransform";
import "./performances.scss";

const PerformancesListPage = ({ engagementType, navigationPath }) => {
  const ROWS_PER_PAGE = 10;
  const filterTransform = useMemo(
    () => createPerformanceFilterTransform(engagementType),
    [engagementType]
  );

  return (
    <ListingComponentProvider
      query={listEngagements}
      fetchPolicy="cache-and-network"
      queryKey="listEngagements"
      initialFilterData={{
        engagementStatus: [],
        engagementType: engagementType ? [engagementType] : [],
        limit: ROWS_PER_PAGE,
        offset: 0,
      }}
      noPagination={true}
      navigationPath={navigationPath}
      filterTransform={filterTransform}
    >
      <PerformancesListContent
        engagementType={engagementType}
        rowsPerPage={ROWS_PER_PAGE}
      />
    </ListingComponentProvider>
  );
};

const PerformancesListContent = ({ engagementType, rowsPerPage }) => {
  const navigate = useNavigate();

  const {
    filterData,
    setFilterData,
    viewableRecords,
    handleClearFilters,
    setViewAllFilters,
    refetch,
  } = useContext(ListingComponentContext);

  const { programTimezone, globalState } = useContext(GlobalConfigContext) || {};
  const programId = localStorage.getItem("programId") || "";
  const { setIsLoading } = useContext(LoaderContext);

  const [toastData, setToastData] = useState([]);

  const [showDrawer, setShowDrawer] = useState(false);

  const [performanceID, setPerformanceID] = useState(null);
  const [engagementItem, setEngagementItem] = useState(null);

  const [modalType, setModalType] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const slug = engagementType === undefined ? "performances-list" : engagementType;
  const { data: performancesUIConfig } = useQuery(loadUiComponent, {
    variables: { slug },
  });

  const tableConfig = performancesUIConfig?.loadUiComponent?.uiSchema?.tableConfig;

  const filterSchemas =
    performancesUIConfig?.loadUiComponent?.uiSchema?.filterConfig;

  const allFiltersSchemas =
    performancesUIConfig?.loadUiComponent?.uiSchema?.allFilterConfig;

  const activityTypeDisplayNames = useMemo(
    () =>
      getActivityTypeDisplayNames(filterSchemas?.withoutDebounce, allFiltersSchemas),
    [filterSchemas, allFiltersSchemas]
  );

  useEffect(() => {
    if (filterData) {
      setCurrentPage(Math.ceil(filterData.offset / rowsPerPage) + 1);
    }
  }, [filterData, rowsPerPage]);

  useEffect(() => {
    setFilterData((prev) => ({
      ...prev,
      engagementType: engagementType ? [engagementType] : [],
      offset: 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementType]);

  const handleModalOpen = (type) => setModalType(type);
  const handleModalClose = () => setModalType(null);

  const { handleEngagementAction } = useEngagementActions({
    setIsLoading,
    setToastData,
    handleModalClose,
    refetch,
    programId,
    globalState,
  });

  const handleOnTap = (item) => {
    setPerformanceID(item.id);
    setEngagementItem(item);
    setShowDrawer(true);
  };

  const handleOnDetailViewTap = (performanceID) => {
    navigate(
      `${engagementType === "all" ? "../" : ""}${paths.performance_details}/${performanceID}`
    );
  };

  const handleChangePage = (page) => {
    setFilterData((prev) => ({
      ...prev,
      offset: (page - 1) * rowsPerPage,
    }));
  };

  const pageTitles = {
    "package-scan": "Package Scans",
    "check-in": "Scan QR Code at location",
    "code-scan-generic": "Code Scans",
  };

  const totalPages = Math.ceil(viewableRecords?.total / rowsPerPage);

  const onActionClick = (action, data) => {
    handleEngagementAction(action, engagementItem, data);
  };

  return (
    <div className="px-3 pt-3">
      {toastData && toastData?.length > 0 && (
        <ToastContainer
          className="performances-listing-toast"
          data={toastData}
          removeToast={() => setToastData([])}
          showActionIcon
        />
      )}
      <div className="container-card">
        <div className="performances-page-content">
          <div className="display-flex align-items-center justify-content-space-between">
            <div className="performances-header">
              <h2>{pageTitles[engagementType] || "Participation List"}</h2>
              <span className="total-results-count">
                {Number(viewableRecords?.total || 0).toLocaleString()}{" "}
                {`${Number(viewableRecords?.total) > 1 ? "Results" : "Result"}`}
              </span>
            </div>
            <FileHistoryButton
              contentType="text/csv"
              context="USERS_PERFORMANCES"
              defaultTab="exports"
              variant="secondary"
            />
          </div>
          <ListingComponentWrapper
            customStyles={{ flex: 1 }}
            filterClasses={
              filterSchemas?.parentClassName
                ? "performances-filter-styles " + filterSchemas?.parentClassName
                : "performances-filter-styles"
            }
            filterConfig={{
              withDebounce: {
                jsonSchema: filterSchemas?.withDebounce?.jsonSchema,
                uiSchema: filterSchemas?.withDebounce?.uiSchema,
              },
              withoutDebounce: {
                jsonSchema: filterSchemas?.withoutDebounce?.jsonSchema,
                uiSchema: filterSchemas?.withoutDebounce?.uiSchema,
              },
              allFilters: {
                jsonSchema: allFiltersSchemas?.jsonSchema,
                uiSchema: allFiltersSchemas?.uiSchema,
              },
            }}
            toggleViewType={false}
            showAllFiltersOption={true}
            exportOptions={true}
            exportOptionsConfig={{
              context: "USERS_PERFORMANCES",
              filename: `performances-list-${Date.now()}.csv`,
            }}
            exportFilterDefaults={{
              engagementStatus: [],
              engagementType: [],
              ...filterData,
            }}
            offsetBasedPagination={true}
          >
            <PerformancesTable
              currentProgramTimezone={programTimezone}
              performancesData={viewableRecords?.items || []}
              engagementType={engagementType}
              onTap={(data) => handleOnTap(data)}
              onResetAllFilters={() => handleClearFilters()}
              onChangeFilters={() => setViewAllFilters(true)}
              tableConfig={tableConfig}
              activityTypeDisplayNames={activityTypeDisplayNames}
            />
            {totalPages > 1 && (
              <div className="pagination-wrap mb-4">
                <Pagination
                  currentPage={currentPage}
                  onUpdate={handleChangePage}
                  pageCount={totalPages}
                  size="medium"
                  variant="number"
                />
              </div>
            )}
          </ListingComponentWrapper>
        </div>
      </div>
      {showDrawer && (
        <PerformanceDetailsDrawer
          showDrawer={showDrawer}
          engagementItem={engagementItem}
          onCloseDrawer={() => setShowDrawer(false)}
          onDetailViewClick={() => handleOnDetailViewTap(performanceID)}
          onApprove={() => {
            setShowDrawer(false);
            handleModalOpen("approve");
          }}
          onReject={() => {
            setShowDrawer(false);
            handleModalOpen("reject");
          }}
        />
      )}
      {modalType === "approve" && (
        <ConfirmationModal
          showModal={modalType === "approve"}
          onClose={handleModalClose}
          title="Approve"
          onConfirm={(data) => onActionClick("approve", data)}
        />
      )}

      {modalType === "reject" && (
        <ConfirmationModal
          showModal={modalType === "reject"}
          onClose={handleModalClose}
          title="Reject"
          onConfirm={(data) => onActionClick("reject", data)}
        />
      )}
    </div>
  );
};

export default PerformancesListPage;
